import { type ReactNode, useContext, useState } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { InfoIcon } from "@phosphor-icons/react/Info";
import { Icon } from "../Icons/Icon";
import { PortalContextReset } from "../Controls/Internal/PortalContextReset";
import { TableHeaderContext } from "../Table/Internal/TableHeaderContext";
import { useTooltipZIndex } from "../Theme/overlayZIndex";
import { cutOutBackdropClasses } from "./Internal/cutOutBackdrop";

type TooltipSide = "top" | "right" | "bottom" | "left";

/**
 * Which trigger the tooltip draws for itself. `info`, `error` and `warning`
 * ignore `children` — the variant *is* the trigger.
 *
 * - `info` — the brand-blue medallion. Every "hover this for an explanation"
 *   mark in every app is this one; there is deliberately no second help mark.
 * - `error` / `warning` — the same medallion, tinted, and smaller because it
 *   trails a validation message rather than a label. Same glyph on purpose: a
 *   help mark that changed shape when it changed colour would read as a
 *   different kind of thing.
 */
type TooltipVariant = "default" | "info" | "error" | "warning";

interface TooltipProps {
  content: ReactNode;
  children?: ReactNode;
  /** Which trigger to draw — see `TooltipVariant`. */
  variant?: TooltipVariant;
  /**
   * Which side of the trigger the tooltip renders on. Defaults to `top` for
   * the default variant, `bottom` for info, and `right` for error/warning
   * (which sit at the end of a message line). Radix still flips the side when
   * collision detection kicks in.
   */
  side?: TooltipSide;
  /**
   * Align the trigger wrapper on the text baseline. The wrapper is an
   * `inline-flex`, whose default (non-baseline) alignment makes it report its
   * bottom edge as the baseline — so a wrapped chip drifts upward inside a
   * baseline-aligned row (e.g. Page header stats). Set this when the trigger
   * is inline text/chip content sitting in such a row. Harmless elsewhere
   * (a single shrink-wrapped child renders identically either way).
   */
  baseline?: boolean;
  /**
   * Shrinks the `info` medallion for dense contexts. Stat bars and table
   * headers pass `sm` so the disc stays proportionate to their tiny uppercase
   * labels. (A table header applies it for you.)
   */
  size?: "default" | "sm";
  /**
   * Renders the trigger wrapper as a block-level flex. The default
   * `inline-flex` wrapper makes two ADJACENT wrapped triggers share one line
   * box — e.g. two consecutive disabled menu items (each wrapped for its
   * disabledMessage) collapsing onto a single menu row. Block-level children
   * (menu items, list rows) pass this.
   */
  block?: boolean;
  /**
   * Make the trigger wrapper fill its container. Set this when the child is a
   * full-width control: the wrapper becomes the flex item, so without it the
   * child's `w-100%` resolves against a shrink-wrapped span and a full-width
   * button silently narrows to its label. `Button` passes it for you whenever
   * it is `fullWidth`.
   */
  stretch?: boolean;
  /**
   * The content is an explanation rather than a short label, so it gets the
   * roomier card and a comfortable 14px base instead of the compact 12px.
   *
   * `info` / `error` / `warning` imply this — they always carry help content.
   * Set it on a `default` tooltip whose trigger has to be your own element
   * (those variants render their own icon) but whose content is still a
   * sentence: the pickup calendar explaining why a day is unavailable, say.
   */
  roomy?: boolean;
  /**
   * How long the pointer must rest on the trigger before the tooltip opens, in
   * milliseconds. Defaults to 0 — instant, which is what a lone trigger wants.
   * Pass `DENSE_GRID_TOOLTIP_DELAY_MS` when triggers are packed tightly.
   */
  delay?: number;
  /**
   * Hold the tooltip open regardless of the pointer. For content the user
   * shouldn't have to discover: a field's warning explanation opens while the
   * field has focus, so nobody has to know the icon is hoverable.
   *
   * Hover still works on top of this — it only forces open, never forces shut.
   */
  forceOpen?: boolean | undefined;
  /**
   * Let the pointer into the card. Two reasons to: the content has something
   * to click — a link, an action — or it is text the reader wants to select
   * and copy (a truncated cell shown in full, an id, an error message headed
   * for a ticket).
   *
   * Off by default: a tooltip is normally read-only, and one that accepts the
   * pointer swallows hovers and clicks meant for whatever it happens to be
   * covering. With the pointer locked out, anything aimed at the card falls
   * through onto the trigger underneath — and pressing the trigger is what
   * closes a tooltip. So the link never gets clicked, and the text vanishes
   * the moment you reach for it.
   *
   * Radix keeps the card open while the pointer travels from the trigger into
   * it, so both are reachable by mouse. Neither is reachable by keyboard —
   * tooltip content never takes focus. Anything a keyboard user must be able to
   * reach belongs in a Popover, not in here.
   */
  interactive?: boolean;
}

/**
 * Gap between the trigger and the card. An explanation (`roomy` — the info /
 * error / warning marks, and anything that opts in) sits further out: it is a
 * paragraph rather than a label, so it needs to read as a panel about the
 * field rather than something touching it. A terse label tooltip stays tight
 * to what it names.
 */
const SIDE_OFFSET = 6;
const ROOMY_SIDE_OFFSET = 14;

/**
 * Hover delay for triggers sitting shoulder-to-shoulder — calendar day cells,
 * for instance. An instant tooltip there pops open the moment the pointer
 * crosses a cell on its way somewhere else, and lands on top of the neighbour
 * the user was actually heading for. Long enough that passing through is
 * silent, short enough that resting on a cell still feels immediate.
 */
export const DENSE_GRID_TOOLTIP_DELAY_MS = 500;

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function Tooltip({
  content,
  children,
  variant = "default",
  side,
  baseline,
  size = "default",
  block,
  stretch,
  roomy,
  delay = 0,
  forceOpen,
  interactive,
}: TooltipProps) {
  const tooltipZ = useTooltipZIndex();
  // Radix drives its own open state until we pass `open`. Tracking the hover
  // ourselves keeps that behaviour intact while letting `forceOpen` OR into it,
  // so a focused field shows its explanation and the pointer still works.
  const [hoverOpen, setHoverOpen] = useState(false);
  // In a table header the info bubble shrinks to match the small uppercase
  // header label (unless a caller already asked for `sm`).
  const inTableHeader = useContext(TableHeaderContext);
  const small = size === "sm" || inTableHeader;
  const annotation = variant === "error" || variant === "warning";
  const trigger = annotation ? (
    // The same medallion as `info`, tinted to the message it annotates and
    // sized in `em` so it tracks whatever message text it trails — those
    // messages run from 12px to 14px, and a fixed disc looks bolted on beside
    // the smaller ones. `ml-0.5` rather than `ml-1`: it should read as part of
    // the sentence it follows, not as a separate control.
    <span className="relative ml-0.5 inline-flex shrink-0 cursor-help transition-opacity duration-150 hover:opacity-80">
      {/* The "i" is cut out of the disc, so it needs a backdrop — see
          `cutOutBackdropClasses`. The page colour, not white: `brand-text`
          lightens in dark mode, so a white mark on it falls to 2.3:1 while the
          page colour gives 8.5:1. */}
      <span className={`${cutOutBackdropClasses} bg-background`} />
      <InfoIcon
        weight="fill"
        className={`relative size-[1em] shrink-0 ${variant === "error" ? "text-red" : "text-orange"}`}
      />
    </span>
  ) : variant === "info" ? (
    // A solid brand-blue info medallion (filled `Info` glyph) — prominent and
    // discoverable. No "?" anywhere on the platform (it reads as uncertainty)
    // and no pointer cursor (hoverable, not clickable); hover dims it slightly.
    // `sm` (stat bars, table headers) drops a size to match the tiny labels.
    // `ml-1` because the medallion nearly always follows a label, and JSX eats
    // the newline between the words and the element — so without it the disc
    // touches the last letter. Callers that write their own `{" "}` were the
    // only ones that looked right, and there is no reason for that to be each
    // caller's problem.
    <span className="relative ml-1 inline-flex shrink-0 cursor-help transition-opacity duration-150 hover:opacity-80">
      {/* The "i" is cut out of the disc, so it needs a backdrop — see
          `cutOutBackdropClasses`. The page colour, not white: `brand-text`
          lightens in dark mode, so a white mark on it falls to 2.3:1 while the
          page colour gives 8.5:1. */}
      <span className={`${cutOutBackdropClasses} bg-background`} />
      <span className="relative inline-flex">
        <Icon
          icon={InfoIcon}
          weight="fill"
          size={small ? "sm" : "md"}
          color="text-brand-text"
        />
      </span>
    </span>
  ) : (
    children
  );

  const resolvedSide =
    side ?? (variant === "info" ? "bottom" : annotation ? "right" : "top");
  // Info and error/warning tooltips carry rich help content (title +
  // paragraphs + a link), so they get a roomier card and a comfortable 14px
  // base — the content uses sizeless <T>, which inherits this. Default
  // tooltips are terse labels ("Copy", "Coming soon") and stay compact at 12px
  // unless the caller says otherwise.
  const isRoomy = roomy ?? (variant === "info" || annotation);
  const maxWidthClass = isRoomy ? "max-w-80" : "max-w-72";
  const textSizeClass = isRoomy ? "text-sm" : "text-xs";
  const paddingClass = isRoomy ? "px-4 py-3" : "px-3 py-2";
  // A terse label ("Copy", "Coming soon") is a chip and carries a little
  // weight. An explanation is prose — sentences set in medium read as shouted,
  // and leave a bolded title nothing to stand out against.
  const fontWeightClass = isRoomy ? "font-normal" : "font-medium";

  return (
    <TooltipPrimitive.Provider delayDuration={delay}>
      <TooltipPrimitive.Root
        open={forceOpen === true || hoverOpen}
        onOpenChange={setHoverOpen}
      >
        {/* The wrapper span is load-bearing, not decoration: it is what makes a
            tooltip on a DISABLED control work at all. Radix opens on the
            trigger's own pointer/focus events, and a disabled button receives
            neither — browsers don't dispatch mouse events to disabled form
            controls, and `disabledStyles` adds `pointer-events: none` on top,
            so it isn't even hit-tested. The hover lands on this span instead.
            Since `disabledMessage` is required whenever `disabled` is set, that
            is the single most common tooltip we render.

            That wrapper used to silently narrow a full-width child: in a
            stretch flex container the span becomes the flex item, so the
            child's `w-full` resolved against a shrink-wrapped span. A disabled
            full-width button — a payment dialog's Confirm with nothing selected
            — collapsed to the width of its label. `stretch` is the fix asked
            for in the note that used to be here: keep the hoverable box AND
            pass the stretch through. Button sets it whenever it is
            `fullWidth`. */}
        <TooltipPrimitive.Trigger asChild>
          <span
            // `align-middle -top-px` on the self-drawn marks, and both parts are
            // load-bearing.
            //
            // This span is an `inline-flex` whose only content is an SVG, so it
            // has no text baseline of its own — browsers fall back to its bottom
            // margin edge, which puts the disc's BOTTOM on the text baseline and
            // floats the whole mark ~5px above the line. `align-middle` fixes
            // that, but only to the halfway point of the x-height: a label's
            // visual centre is half its CAP height, which is ~1.2px higher, so
            // the disc still reads as hanging low beside words with no
            // descenders ("Handling Units", "Description"). `-top-px` closes it;
            // measured residual is under a quarter-pixel at 12, 14 and 16px. A
            // proportional `em` nudge was worse — `vertical-align` replaces the
            // `middle` it would be correcting.
            //
            // Only inline flow is affected, so callers who happened to wrap a
            // mark in a flex Row never saw the 5px and never needed to. Inside a
            // flex parent `vertical-align` is ignored, so this can't disturb
            // them.
            //
            // Not applied to `default`, whose child is the caller's own element
            // and whose alignment is theirs to own.
            className={`relative ${block ? "flex" : "inline-flex"}${stretch === true ? " w-full" : ""}${baseline ? " items-baseline" : ""}${variant === "default" ? "" : " -top-px align-middle"}`}
          >
            {trigger}
          </span>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            style={{ zIndex: tooltipZ }}
            side={resolvedSide}
            sideOffset={isRoomy ? ROOMY_SIDE_OFFSET : SIDE_OFFSET}
            collisionPadding={8}
            // `group` so descendants can react to Radix's post-flip
            // `data-side` for the tab depth's counter-rotation.
            //
            // `--tooltip-slide-x/y` feeds the directional slide-in distance
            // to the `tooltip-in` keyframe, set per `data-side` so the
            // tooltip always slides from the trigger-facing direction.
            //
            // `pointer-events-none`: the tooltip is read-only, so it should
            // never swallow a hover or a click meant for whatever it happens
            // to be covering. `interactive` is the exception — a card holding
            // a link, or text worth copying, has to be reachable, and with the
            // pointer locked out anything aimed at the card lands on the
            // trigger instead, which is exactly what closes the tooltip.
            className={`group${interactive === true ? "" : " pointer-events-none"} ${maxWidthClass} data-[side=bottom]:[--tooltip-slide-y:-12px] data-[side=left]:[--tooltip-slide-x:12px] data-[side=right]:[--tooltip-slide-x:-12px] data-[side=top]:[--tooltip-slide-y:12px] data-[state=closed]:animate-tooltip-out data-[state=delayed-open]:animate-tooltip-in data-[state=instant-open]:animate-tooltip-in`}
          >
            {/* a clean flat card — no offset "keycap" depth and no arrow
                tab (the odd dot). Just the surface + border + soft shadow. */}
            <div
              className={`rounded-md border border-border bg-card ${paddingClass} ${textSizeClass} ${fontWeightClass} text-foreground shadow-lg`}
            >
              <PortalContextReset>{content}</PortalContextReset>
            </div>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

export type { TooltipVariant };

interface TooltipRow {
  label: string;
  value: ReactNode;
}

interface TooltipContentProps {
  rows: TooltipRow[];
  /** Allow values to wrap inside the table cell. Use for moderately long text like shipping names. */
  wrap?: boolean | undefined;
  /** Stack each label above its value (full-width) instead of side-by-side. Use for long free-text or emails. Includes wrap-anywhere so unbreakable strings break cleanly. */
  stacked?: boolean | undefined;
}

/** Structured key-value content for use inside Tooltip. */
export function TooltipContent({ rows, wrap, stacked }: TooltipContentProps) {
  if (stacked) {
    return (
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-col gap-0.5">
            <div className="text-[11px] font-bold tracking-wide text-popover-foreground/50 uppercase">
              {row.label}
            </div>
            <div className="text-xs font-medium wrap-anywhere text-popover-foreground">
              {row.value}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <table className="w-full border-collapse">
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            {/* The label wraps (`wrap-anywhere`) so the table's minimum width
                can always fit inside the tooltip's max-width. A table never
                shrinks below its minimum, so a nowrap label plus a nowrap
                value would push the value column outside the tooltip box
                instead of wrapping. Values keep their nowrap so dates and
                numbers never break mid-value — the label gives instead. */}
            <td className="py-0.5 pr-3 align-top text-[11px] font-bold tracking-wide wrap-anywhere text-popover-foreground/50 uppercase">
              {row.label}
            </td>
            <td
              className={`py-0.5 align-top text-xs font-medium text-popover-foreground ${wrap ? "wrap-anywhere" : "whitespace-nowrap"}`}
            >
              {row.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
